import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from 'lucide-react';
import { fetchAccountTransactions, AccountTransaction } from '@/services/accountService';
import { format } from 'date-fns';

interface AccountTransactionHistoryProps {
  accountId: string;
  accountName: string;
}

export function AccountTransactionHistory({ accountId, accountName }: AccountTransactionHistoryProps) {
  const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchAccountTransactions(accountId, startDate, endDate);
      setTransactions(data);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [accountId, startDate, endDate]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-EG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + ' EGP';
  };

  const getTransactionTypeLabel = (type: string) => {
    switch (type) {
      case 'credit':
        return 'دائن / Credit';
      case 'debit':
        return 'مدين / Debit';
      case 'adjustment':
        return 'تسوية / Adjustment';
      default:
        return type;
    }
  };

  const getReferenceTypeLabel = (type: string) => {
    switch (type) {
      case 'order':
        return 'طلب / Order';
      case 'expense':
        return 'مصروف / Expense';
      case 'transfer':
        return 'تحويل / Transfer';
      case 'manual':
        return 'يدوي / Manual';
      case 'opening_balance':
        return 'رصيد افتتاحي / Opening Balance';
      default:
        return type;
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'credit':
        return 'text-green-600';
      case 'debit':
        return 'text-red-600';
      case 'adjustment':
        return 'text-blue-600';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="text-center">جاري التحميل... / Loading...</div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {accountName} - سجل المعاملات / Transaction History
        </h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setStartDate(undefined);
              setEndDate(undefined);
            }}
          >
            <Calendar className="h-4 w-4 mr-2" />
            عرض الكل / Show All
          </Button>
        </div>
      </div>

      {transactions.length === 0 ? (
        <Card className="p-6">
          <div className="text-center text-muted-foreground">
            لا توجد معاملات / No transactions found
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="p-3 text-right">التاريخ / Date</th>
                  <th className="p-3 text-right">الوصف / Description</th>
                  <th className="p-3 text-right">النوع / Type</th>
                  <th className="p-3 text-right">المرجع / Reference</th>
                  <th className="p-3 text-right">المبلغ / Amount</th>
                  <th className="p-3 text-right">الرصيد بعد / Balance After</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="border-b hover:bg-muted/50">
                    <td className="p-3">
                      {format(new Date(transaction.transaction_date), 'yyyy-MM-dd HH:mm')}
                    </td>
                    <td className="p-3">{transaction.description}</td>
                    <td className={`p-3 font-semibold ${getTransactionTypeColor(transaction.transaction_type)}`}>
                      {getTransactionTypeLabel(transaction.transaction_type)}
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {getReferenceTypeLabel(transaction.reference_type)}
                      {transaction.reference_id && (
                        <span className="block text-xs">#{transaction.reference_id.slice(0, 8)}</span>
                      )}
                    </td>
                    <td className={`p-3 font-semibold ${getTransactionTypeColor(transaction.transaction_type)}`}>
                      {transaction.transaction_type === 'credit' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="p-3 font-bold">
                      {formatCurrency(transaction.balance_after)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="text-sm text-muted-foreground">
        إجمالي المعاملات / Total Transactions: {transactions.length}
      </div>
    </div>
  );
}
